import {
  getAttributesFromExtensions,
  getExtensionField,
  getRenderedAttributes,
  mergeAttributes,
} from "@tiptap/core";
import { DOMSerializer } from "@tiptap/pm/model";

import {
  clearCollapsedFlag,
  getCollapseType,
  isCollapsed,
} from "@/components/tiptap/collapse/utils";
import { editorTranslator } from "@/components/tiptap/EditorTranslator";
import { handleLineNumberClick } from "@/components/tiptap/LineNumbers";
import { isLocked } from "@/components/tiptap/lock/utils";
import { setLineWrapperGetPos } from "@/components/tiptap/utils/line-wrapper/lineWrapperGetPos";
import {
  renderLineWrapperHTML,
  type Props as LineWrapperProps,
} from "@/components/tiptap/utils/line-wrapper/renderLineWrapperHTML";

import type { DropdownToggleButtonProps } from "@/components/sidebar/Dropdown";
import type { DocumentModel } from "@/models/DocumentModel";
import type { DOMNode, Editor, NodeViewRendererProps, NodeConfig } from "@tiptap/core";
import type { DOMOutputSpec, Node } from "@tiptap/pm/model";
import type { NodeView } from "@tiptap/pm/view";

export type LineMenuProps = {
  doc: DocumentModel;
  getPos: () => number | undefined;
  onOpenChange?: (isOpen: boolean) => void;
  /** For the button that opens the menu, which the block controls focus from the keyboard. */
  toggleButtonProps?: DropdownToggleButtonProps;
};

export type LineMenu = "none" | LineMenuComponent | undefined;

export type LineMenuComponent = (props?: LineMenuProps) => React.ReactNode;

/**
 * Resolve the `lineMenu` option declared by the node's extension.
 *
 * - `undefined` → default hover menu
 * - `"none"`    → no hover, no buttons
 *
 * @example Disable the hover menu for a block:
 * ```ts
 * const MyBlock = Base.extend({
 *   addOptions() {
 *     return { ...this.parent?.(), lineMenu: "none" as const };
 *   },
 * });
 * ```
 *
 * @example Custom menu (render your own buttons in the overlay):
 * ```ts
 * const MyBlock = Base.extend({
 *   addOptions() {
 *     return { ...this.parent?.(), lineMenu: MyCustomLineMenu };
 *   },
 * });
 * ```
 */
export function getLineMenu(editor: Editor | undefined, node: Node | undefined): LineMenu {
  if (!editor || !node) {
    return undefined;
  }

  const extension = editor.extensionManager.extensions.find((ext) => ext.name === node.type.name);

  return (extension?.options as { lineMenu?: LineMenu } | undefined)?.lineMenu;
}

type Options = {
  renderNodeAsChildIn?: string[];
  fallbackTag?: string;
} & LineWrapperProps;

export class LineWrapperView implements NodeView {
  node: Node;

  editor: Editor;

  dom: DOMNode;

  contentDOM: HTMLElement | undefined;

  getPos: () => number | undefined;

  private lineLink?: HTMLElement | null;

  private detachCollapseHandlers?: () => void;

  constructor(
    { node, getPos, editor }: NodeViewRendererProps,
    { renderNodeAsChildIn = [], className, fallbackTag = "div", ...lineWrapperProps }: Options = {},
  ) {
    this.node = node;
    this.getPos = getPos;
    this.editor = editor;

    const t = editorTranslator(editor) ?? undefined;

    let attributes: Record<string, unknown> = {};

    const extension = editor.extensionManager.extensions.find((ex) => ex.name === node.type.name);
    const options = extension?.options as { HTMLAttributes?: Record<string, unknown> };

    const pos = getPos();
    let showLineNumber = false;
    let showLineOptionButtons = false;
    let renderAsChild = false;
    // The collapsible block's user-facing name. When the line is a paragraph wrapped
    // in a callout/quote, the meaningful block is the wrapper, not the paragraph.
    let topLevelNodeTypeName = node.type.name;

    if (pos) {
      const { parent } = editor.state.doc.resolve(pos);
      const lineMenu = getLineMenu(editor, node);
      const parentLineMenu = getLineMenu(editor, parent);
      const hasLineMenu = lineMenu !== "none";

      renderAsChild = [node.type.name, ...renderNodeAsChildIn].includes(parent.type.name);
      showLineNumber =
        parent.type.name === "documentTitleSchema" ||
        parent.type.name === "document" ||
        parentLineMenu === "none";
      showLineOptionButtons = showLineNumber && hasLineMenu && parentLineMenu !== "none";

      if (parent.type.name !== "document" && parent.type.name !== "documentTitleSchema") {
        topLevelNodeTypeName = parent.type.name;
      }
    }

    const collapseType = showLineNumber ? getCollapseType(node) : undefined;
    const collapsed = !!collapseType && isCollapsed(node);
    const locked = isLocked(node);

    let childSpec: DOMOutputSpec | null = null;

    if (extension) {
      const context = {
        name: extension.name,
        storage: extension.storage as unknown,
        options,
        editor,
      };
      const allAttributes = getAttributesFromExtensions(this.editor.extensionManager.extensions);
      const extensionAttributes = allAttributes.filter(
        (attribute) => attribute.type === node.type.name,
      );

      const renderHTML = getExtensionField<NodeConfig["renderHTML"]>(
        extension,
        "renderHTML",
        context,
      );
      attributes = getRenderedAttributes(this.node, extensionAttributes);

      if (renderHTML) {
        childSpec = renderHTML({
          node: this.node,
          HTMLAttributes: mergeAttributes(attributes, {
            class: renderAsChild ? className : "",
          }),
        });
      }
    }

    if (!childSpec) {
      childSpec = [
        fallbackTag,
        mergeAttributes(options?.HTMLAttributes ?? {}, attributes, {
          class: renderAsChild ? className : "",
        }),
        0,
      ];
    }

    if (!renderAsChild) {
      childSpec = renderLineWrapperHTML(["div", { class: "relative w-full" }, childSpec], {
        className,
        showLineNumber,
        showLineOptionButtons,
        collapseType,
        collapsed,
        locked,
        topLevelNodeTypeName,
        t,
        ...lineWrapperProps,
      });
    }

    const { dom, contentDOM } = DOMSerializer.renderSpec(document, childSpec);

    this.dom = dom;
    this.contentDOM = contentDOM;

    this.lineLink = (this.dom as HTMLElement).querySelector(".line-number");
    if (this.lineLink) {
      this.lineLink.addEventListener("click", handleLineNumberClick);
    }

    const element = this.dom as HTMLElement;
    const lineWrapper = element.matches(".line-wrapper")
      ? element
      : element.querySelector<HTMLElement>(".line-wrapper");

    // The block menu acts through it after awaits, so it needs the live position.
    if (lineWrapper) {
      setLineWrapperGetPos(lineWrapper, this.getPos);
    }

    const lockIndicator = (this.dom as HTMLElement).querySelector(".lock-indicator-static");

    if (lockIndicator) {
      setLineWrapperGetPos(lockIndicator as HTMLElement, this.getPos);
    }

    if (collapseType) {
      this.wireCollapseHandlers(collapsed);
    }

    const rendersCollapsed = !renderAsChild && collapsed;
    if (isCollapsed(node) && !rendersCollapsed) {
      this.scheduleStrayCollapsedReset();
    }
  }

  /**
   * Clear a `collapsed` flag left on a block that renders inline (no collapse toggle of its own).
   * Deferred with `queueMicrotask` because a node view can't dispatch a transaction during its own
   * construction.
   */
  private scheduleStrayCollapsedReset() {
    queueMicrotask(() => {
      if (this.editor.isDestroyed) {
        return;
      }

      const pos = this.getPos();
      if (pos !== undefined) {
        clearCollapsedFlag(this.editor, pos);
      }
    });
  }

  /**
   * Wire the collapse toggle button, which only *collapses* — it's the sole collapse
   * affordance for both block and section. Expanding is owned by the click handler
   * (`handleClickOn`): a click anywhere on a collapsed line expands it. So there's nothing
   * to wire once collapsed, and this also avoids the focus/selection race a raw DOM listener
   * hits on the first click.
   */
  private wireCollapseHandlers(collapsed: boolean) {
    if (collapsed) {
      return;
    }

    const root = this.dom as HTMLElement;
    const button = root.querySelector("[data-collapse-toggle]");

    if (!button) {
      return;
    }

    const preserveFocus = (e: Event) => e.preventDefault();

    const collapse = (e: Event) => {
      e.preventDefault();
      e.stopPropagation();

      const pos = this.getPos();

      if (pos !== undefined) {
        this.editor.chain().toggleCollapsed(pos, true).focus().run();
      }
    };

    button.addEventListener("mousedown", preserveFocus);
    button.addEventListener("click", collapse);

    this.detachCollapseHandlers = () => {
      button.removeEventListener("mousedown", preserveFocus);
      button.removeEventListener("click", collapse);
    };
  }

  destroy() {
    if (this.lineLink) {
      this.lineLink.removeEventListener("click", handleLineNumberClick);
      this.lineLink = undefined;
    }

    this.detachCollapseHandlers?.();
    this.detachCollapseHandlers = undefined;
  }
}
